import React, { createContext, useState, useCallback } from "react";
import {
  AllEarningWithdrawRequestsType,
  GetCollabEarningsTypes,
  InfluencerEarningType,
  ReferredInfluencerType,
  useInfluencer,
} from "../../../../../Providers/Influencer";
import { useToast } from "../../../../../shadcnComponents/ui/use-toast";

interface ModalOpenStateType {
  requestWithdrawal: boolean;
}

interface SuccessStateType {
  requestWithdrawal: boolean;
}

interface EarningWalletContextType {
  earnings: InfluencerEarningType | null;
  referrals: ReferredInfluencerType[];
  collabEarnings: GetCollabEarningsTypes[];
  allWithdrawRequests: AllEarningWithdrawRequestsType[];
  showConfetti: boolean;
  modalOpen: ModalOpenStateType;
  success: SuccessStateType;
  loading: boolean;
  fetchEarnings: () => Promise<void>;
  fetchReferrals: () => Promise<void>;
  fetchCollabEarnings: () => Promise<void>;
  handleGetReward: (id: string, index: number) => Promise<void>;
  setModalOpenState: (
    modalName: keyof ModalOpenStateType,
    value: boolean
  ) => void;
  setSuccessState: (
    successState: keyof SuccessStateType,
    value: boolean
  ) => void;
  handleWithdrawRequest: () => Promise<void>;
  handleGetAllWithdrawRequest: () => Promise<void>;
  setShowConfetti: React.Dispatch<React.SetStateAction<boolean>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export const EarningWalletContext = createContext<
  EarningWalletContextType | undefined
>(undefined);

interface InfluEarningWalletProviderProps {
  children: React.ReactNode;
}

export const InfluEarningWalletProvider: React.FC<
  InfluEarningWalletProviderProps
> = ({ children }) => {
  const influencerState = useInfluencer();
  const { toast } = useToast();
  const [earnings, setEarnings] = useState<InfluencerEarningType>(null);
  const [referrals, setReferrals] = useState<ReferredInfluencerType[]>([]);
  const [allWithdrawRequests, setAllWithdrawRequests] = useState<
    AllEarningWithdrawRequestsType[]
  >([]);
  const [showConfetti, setShowConfetti] = useState(false);
  const [collabEarnings, setCollabEarnings] = useState<
    GetCollabEarningsTypes[]
  >([]);
  const [modalOpen, setModalOpen] = useState({ requestWithdrawal: false });
  const [success, setSuccess] = useState({ requestWithdrawal: false });
  const [loading, setLoading] = useState(false);

  const fetchEarnings = useCallback(async () => {
    try {
      const earnings = await influencerState.getEarnings();
      if (earnings?.success) {
        setEarnings(earnings.result);
      } else {
        throw new Error("Failed to fetch earnings");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch earnings. Please try again later.",
        variant: "destructive",
      });
    }
  }, [influencerState, toast]);

  const fetchReferrals = useCallback(async () => {
    try {
      const referrals = await influencerState.getReferrals();
      if (referrals?.success) {
        setReferrals(referrals.result);
      } else {
        throw new Error("Failed to fetch referrals");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch referrals. Please try again later.",
        variant: "destructive",
      });
    }
  }, [influencerState, toast]);

  const fetchCollabEarnings = useCallback(async () => {
    try {
      const collabEarnings = await influencerState?.getCollabEarnings();
      if (collabEarnings?.success) {
        setCollabEarnings(collabEarnings?.result);
      } else {
        throw new Error("Failed to fetch collab earnings");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch collab earnings. Please try again later.",
        variant: "destructive",
      });
    }
  }, [influencerState, toast]);

  const handleGetReward = async (id: string, index: number) => {
    const reward = await influencerState.getReferralReward({ referToId: id });
    if (reward?.success) {
      setReferrals((prev) => {
        const updatedReferrals = [...prev];
        updatedReferrals[index] = {
          ...updatedReferrals[index],
          isClaimed: true,
          claimedDate: new Date().toISOString(),
          rewardAmount: reward?.result?.rewardAmount,
        };
        return updatedReferrals;
      });
      fetchEarnings();

      if (reward?.result?.rewardAmount > 0) {
        setShowConfetti(true);
        setTimeout(() => {
          setShowConfetti(false);
        }, 5000);
      }
    } else {
      toast({
        title: "Failed to get reward",
        description: "Please try again later",
      });
    }
  };

  const setModalOpenState = (
    modalName: keyof typeof modalOpen,
    value: boolean
  ) => {
    setModalOpen((p) => ({ ...p, [modalName]: value }));
  };

  const setSuccessState = (
    successState: keyof typeof success,
    value: boolean
  ) => {
    setSuccess((p) => ({ ...p, [successState]: value }));
  };

  const handleWithdrawRequest = async () => {
    if (earnings?.overall?.totalBalance < earnings?.minWithdrawLimit) {
      toast({
        title: "Error",
        description: `Minimum withdrawal limit is ₹${earnings?.minWithdrawLimit}.`,
        variant: "destructive",
      });
      return;
    }
    try {
      setLoading(true);
      const response = await influencerState.requestWithdrawEarnings({
        amount: earnings?.overall?.totalBalance,
      });
      if (response?.success) {
        await fetchEarnings();
        setSuccessState("requestWithdrawal", true);
        await handleGetAllWithdrawRequest();
      } else {
        throw new Error("Failed to request withdrawal");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to request withdrawal. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGetAllWithdrawRequest = async () => {
    try {
      setLoading(true);
      const response = await influencerState.getEarningWithdrawRequests();
      if (response?.success) {
        setAllWithdrawRequests(response.result);
      } else {
        throw new Error("Failed to fetch all withdraw requests");
      }
    } catch (error) {
      toast({
        title: "Error",
        description:
          "Failed to fetch all withdraw requests. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <EarningWalletContext.Provider
      value={{
        earnings,
        referrals,
        collabEarnings,
        allWithdrawRequests,
        showConfetti,
        modalOpen,
        success,
        loading,
        fetchEarnings,
        fetchReferrals,
        fetchCollabEarnings,
        handleGetReward,
        setModalOpenState,
        setSuccessState,
        handleWithdrawRequest,
        handleGetAllWithdrawRequest,
        setShowConfetti,
        setLoading,
      }}
    >
      {children}
    </EarningWalletContext.Provider>
  );
};
